import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  Modal,
  TextField,
  Button,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Switch,
  FormControlLabel
} from "@mui/material";

const styleModal = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 460,
  bgcolor: "white",
  borderRadius: "10px",
  p: 3,
  boxShadow: 24
};

const roles = [
  { label: "Administrador", value: "ADMIN" },
  { label: "Vendedor", value: "VENDOR" },
  { label: "Superusuario", value: "SUPER" }
];

export default function EditDeleteUsers({ open, mode, user, onClose, getCSRF, onDone }) {
  const isDelete = useMemo(() => mode === "delete", [mode]);

  const [form, setForm] = useState({
    nombre: "",
    correo: "",
    telefono: "",
    rol: "VENDOR",
    activo: true
  });

  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    setForm({
      nombre: user?.nombre || "",
      correo: user?.email || "",
      telefono: user?.telefono || "",
      rol: user?.rol || "VENDOR",
      activo: !!user?.activo
    });
  }, [open, user]);

  const closeSafe = () => {
    if (loading) return;
    onClose();
  };

  const handleChange = (e) => {
    setForm((p) => ({ ...p, [e.target.name]: e.target.value }));
  };

  const doEdit = async () => {
    if (!user?.id) return;
    if (!form.nombre.trim() || !form.correo.trim()) {
      onDone?.("Nombre y correo son obligatorios", "error");
      return;
    }

    setLoading(true);
    try {
      const csrf = await getCSRF();
      const res = await fetch("/sistema/editar-usuario/", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json", "X-CSRFToken": csrf },
        body: JSON.stringify({
          id: user.id,
          nombre: form.nombre.trim(),
          correo: form.correo.trim().toLowerCase(),
          telefono: (form.telefono || "").trim(),
          rol: form.rol,
          activo: !!form.activo
        })
      });
      const d = await res.json();
      if (res.ok && d.ok) {
        onClose();
        onDone?.("Usuario actualizado", "success");
      } else {
        onDone?.(d.error || "No se pudo actualizar", "error");
      }
    } catch {
      onDone?.("Error de conexión", "error");
    } finally {
      setLoading(false);
    }
  };

  const doDelete = async () => {
    if (!user?.id) return;

    setLoading(true);
    try {
      const csrf = await getCSRF();
      const res = await fetch("/sistema/eliminar-usuario/", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json", "X-CSRFToken": csrf },
        body: JSON.stringify({ id: user.id })
      });
      const d = await res.json();
      if (res.ok && d.ok) {
        onClose();
        onDone?.("Usuario eliminado", "success");
      } else {
        onDone?.(d.error || "No se pudo eliminar", "error");
      }
    } catch {
      onDone?.("Error de conexión", "error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal open={open} onClose={closeSafe}>
      <Box sx={styleModal}>
        <Typography sx={{ fontWeight: 900, color: isDelete ? "#C62828" : "#1565C0", mb: 1 }}>
          {isDelete ? "Eliminar usuario" : "Editar usuario"}
        </Typography>

        <Typography sx={{ color: "#555", mb: 2 }}>
          {user?.nombre ? `Usuario: ${user.nombre}` : "Usuario seleccionado"}
        </Typography>

        {isDelete ? (
          <>
            <Typography sx={{ color: "#777", mb: 2 }}>
              Esta acción eliminará el usuario de forma permanente.
            </Typography>

            <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1.5, mt: 2 }}>
              <Button onClick={closeSafe} disabled={loading} sx={{ textTransform: "none" }}>
                Cancelar
              </Button>
              <Button
                variant="contained"
                onClick={doDelete}
                disabled={loading}
                sx={{
                  textTransform: "none",
                  bgcolor: "#C62828",
                  "&:hover": { bgcolor: "#B71C1C" }
                }}
              >
                Eliminar
              </Button>
            </Box>
          </>
        ) : (
          <>
            <TextField
              label="Nombre completo"
              name="nombre"
              fullWidth
              value={form.nombre}
              onChange={handleChange}
              sx={{ mb: 2 }}
            />

            <TextField
              label="Correo"
              name="correo"
              fullWidth
              value={form.correo}
              onChange={handleChange}
              sx={{ mb: 2 }}
            />

            <TextField
              label="Teléfono"
              name="telefono"
              fullWidth
              value={form.telefono}
              onChange={handleChange}
              sx={{ mb: 2 }}
            />

            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Rol</InputLabel>
              <Select label="Rol" name="rol" value={form.rol} onChange={handleChange}>
                {roles.map((r) => (
                  <MenuItem key={r.value} value={r.value}>
                    {r.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControlLabel
              control={
                <Switch
                  checked={!!form.activo}
                  onChange={(e) => setForm((p) => ({ ...p, activo: e.target.checked }))}
                />
              }
              label={form.activo ? "Activo" : "Inactivo"}
              sx={{ mb: 1 }}
            />

            <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1.5, mt: 2 }}>
              <Button onClick={closeSafe} disabled={loading} sx={{ textTransform: "none" }}>
                Cancelar
              </Button>
              <Button
                variant="contained"
                onClick={doEdit}
                disabled={loading}
                sx={{
                  textTransform: "none",
                  bgcolor: "#1565C0",
                  "&:hover": { bgcolor: "#0D47A1" }
                }}
              >
                Guardar cambios
              </Button>
            </Box>
          </>
        )}
      </Box>
    </Modal>
  );
}
